import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchInventoryCars, type InventoryCarItem } from "@/data/carsData";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  BadgeCheck,
} from "lucide-react";

/**
 * Featured Deals Section Component
 *
 * Features:
 * - Horizontal carousel of vehicle cards
 * - Navigation arrows for carousel
 * - Vehicle details and pricing
 * - API-based vehicle data (same source pattern as inventory page)
 */
export default function FeaturedDeals() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cars, setCars] = useState<InventoryCarItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isMounted = true;

    const loadFeaturedCars = async () => {
      try {
        const result = await fetchInventoryCars({
          page: 1,
          limit: 8,
          ordBy: 2,
        });

        if (!isMounted) return;
        setCars(result.cars);
      } catch {
        if (!isMounted) return;
        setCars([]);
      }
    };

    loadFeaturedCars();

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleCards = cars.length > 3 ? 3 : Math.max(cars.length, 1);
  const maxIndex = Math.max(cars.length - visibleCards, 0);

  const scrollToIndex = (index: number) => {
    if (!containerRef.current || cars.length === 0) return;

    const cardWidth = containerRef.current.scrollWidth / cars.length;

    containerRef.current.scrollTo({
      left: cardWidth * index,
      behavior: "smooth",
    });
  };

  const handlePrev = () => {
    if (cars.length === 0) return;

    const newIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    if (cars.length === 0) return;

    const newIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const getCarTitle = (car: InventoryCarItem) =>
    [car.year ? String(car.year) : "", car.make, car.model, car.trim]
      .filter(Boolean)
      .join(" ");

  const formatNumber = (value: number | null) =>
    value === null ? "" : value.toLocaleString();

  const formatCurrency = (value: number | null) =>
    value === null ? "" : value.toLocaleString();

  const getInstallmentHeading = (type?: string) => {
    const cleaned = type?.trim() ?? "";
    if (!cleaned) return "";
    return `Est. ${cleaned.replace(/^by\s+/i, "")}`;
  };

  const getInstallmentSuffix = (type?: string) => {
    const normalized = type?.trim().toLowerCase() ?? "";

    if (!normalized) return "";
    if (normalized.includes("week")) return "/wk";
    if (normalized.includes("month")) return "/mo";
    if (normalized.includes("day")) return "/day";
    if (normalized.includes("year")) return "/yr";

    return "";
  };

  const getInstallmentMeta = (
    percentage?: string,
    duration?: number | null,
  ) => {
    const pct = percentage?.trim() ?? "";
    const dur = duration ?? null;

    if (pct && dur !== null) return `${pct}% for ${dur} months`;
    if (pct) return `${pct}%`;
    if (dur !== null) return `${dur}`;
    return "";
  };

  const getCarChips = (car: InventoryCarItem): string[] =>
    [
      car.bodyStyle?.trim(),
      car.transmission?.trim(),
      car.km !== null ? `${formatNumber(car.km)} KM` : "",
      car.fuelType?.trim(),
    ].filter((value): value is string => Boolean(value));

  return (
    <section id="inventory" className="py-14 bg-gray-50 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
        >
          <div>
            <h2 className="font-serif text-4xl md:text-4xl font-bold text-gray-900">
              FEATURED <span className="text-brand-red">DEALS</span>
            </h2>
            <p className="text-gray-600 mt-3 text-lg">
              Exceptional vehicles at unbeatable prices
            </p>
          </div>

          {/* Navigation Arrows */}
          <div className="flex gap-3 mt-6 md:mt-0">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-brand-red hover:text-white hover:border-brand-red transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-brand-red hover:text-white hover:border-brand-red transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </motion.div>

        {/* Cars Carousel */}
        <div className="relative overflow-hidden">
          <div
            ref={containerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {cars.map((car, index) => {
              const isSold = car.isSold;
              const detailChips = getCarChips(car);
              const showHistoryBadge = Boolean(car.carfxUrl?.trim());

              return (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                  className="flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-start"
                >
                  <div
                    className={`rounded-2xl overflow-hidden shadow-card car-card h-full flex flex-col border transition-all duration-300 ${
                      isSold
                        ? "bg-gradient-to-b from-gray-50 to-gray-100 border-gray-300"
                        : "bg-white border-transparent"
                    }`}
                  >
                    {/* Image */}
                    <div className="relative h-52 bg-gray-100 overflow-hidden">
                      {car.image ? (
                        <img
                          src={car.image}
                          alt={getCarTitle(car) || "Vehicle"}
                          className={`w-full h-full object-cover transition-transform duration-500 ${
                            isSold ? "grayscale brightness-[0.72]" : ""
                          }`}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100" />
                      )}

                      {car.isGoodPrice && car.tagLabel && (
                        <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 z-20">
                          <BadgeCheck className="w-4 h-4" />
                          {car.tagLabel}
                        </div>
                      )}

                      {isSold && (
                        <div className="absolute top-4 right-[-42px] z-30 w-[160px] rotate-45 overflow-hidden border border-white/20 bg-gradient-to-r from-red-500 via-red-600 to-red-800 py-2 text-center shadow-[0_12px_28px_rgba(185,28,28,0.38)] pointer-events-none">
                          <span className="block text-[11px] font-extrabold uppercase tracking-[0.22em] text-white drop-shadow-sm">
                            Sold Out
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div
                      className={`p-6 flex-1 flex flex-col ${isSold ? "opacity-90" : ""}`}
                    >
                      <h3 className="font-serif text-xl font-bold text-gray-900 mb-4">
                        {getCarTitle(car)}
                      </h3>

                      {/* Pricing */}
                      <div className="flex gap-6 mb-4 pb-4 border-b border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide min-h-[16px]">
                            {getInstallmentHeading(car.installmentType)}
                          </p>

                          <p className="text-2xl font-bold text-brand-red min-h-[32px]">
                            {car.installmentAmount !== null ? (
                              <>
                                ${formatCurrency(car.installmentAmount)}
                                {getInstallmentSuffix(car.installmentType) && (
                                  <span className="text-sm font-normal text-gray-500">
                                    {getInstallmentSuffix(car.installmentType)}
                                  </span>
                                )}
                              </>
                            ) : (
                              ""
                            )}
                          </p>

                          <p className="text-xs text-gray-400 min-h-[18px]">
                            {getInstallmentMeta(
                              car.installmentPercentage,
                              car.installmentDuration,
                            )}
                          </p>
                        </div>

                        <div className="border-l border-gray-200 pl-6">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Sale Price
                          </p>
                          <p className="text-2xl font-bold text-gray-900 min-h-[32px]">
                            {car.price !== null
                              ? `$${formatCurrency(car.price)}`
                              : ""}
                          </p>
                          <p className="text-xs text-gray-400">+ Tax & Fees</p>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="mb-6">
                        <div className="flex flex-wrap gap-2">
                          {detailChips.map((chip, chipIndex) => (
                            <span
                              key={`${car.id}-chip-${chipIndex}`}
                              title={chip}
                              className={`inline-flex max-w-full items-center rounded-full border px-4 py-2 text-sm font-medium leading-none ${
                                isSold
                                  ? "border-gray-300 bg-gray-200 text-gray-600"
                                  : "border-gray-200 bg-gray-100 text-gray-700"
                              }`}
                            >
                              <span className="truncate whitespace-nowrap">
                                {chip}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      {/* CTA */}
                      <div
                        className={`mt-auto ${showHistoryBadge ? "grid grid-cols-2 gap-3" : ""}`}
                      >
                        {showHistoryBadge && (
                          <a
                            href={car.carfxUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center px-2 py-1 cursor-pointer"
                            aria-label={`Open vehicle history report for ${getCarTitle(car)}`}
                            title="Open vehicle history report"
                          >
                            <img
                              src="/images/car-fax-logo.jpeg"
                              alt="Vehicle History"
                              className="h-8 w-auto max-w-full object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          </a>
                        )}

                        <Link
                          to={`/vehicle-details/${car.id}`}
                          className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-300 group ${
                            showHistoryBadge ? "w-full" : "w-full mt-auto"
                          } ${
                            isSold
                              ? "border-gray-300 text-gray-500 bg-gray-200 hover:bg-gray-300 hover:border-gray-400"
                              : "border-gray-200 text-gray-700 hover:border-brand-red hover:text-brand-red"
                          }`}
                        >
                          DETAILS
                          <ArrowRight
                            className={`w-4 h-4 transition-transform ${
                              isSold ? "" : "group-hover:translate-x-1"
                            }`}
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-4"
        >
          <Link
            to="/car-inventory"
            className="inline-flex items-center gap-2 px-8 py-4 bg-brand-red text-white rounded-full font-semibold hover:bg-red-700 transition-all duration-300 hover:-translate-y-1 shadow-glow-red"
          >
            View All Inventory
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
